/**
 * useSentDocumentsList Hook
 *
 * Business logic for sent documents list view including:
 * - Fetching pre-grouped sent documents from the API
 * - Cursor-based pagination (Prev/Next, no totals)
 * - Multi-select filtering by channel, status, document type
 * - Filtering by patient, date range
 * - Sorting by created_at, sent_at, delivered_at
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Dayjs } from 'dayjs';

import { useLazyGetSentDocumentsQuery } from '../store/sentDocumentsApi';
import type { SentDocumentGroup, CommunicationItem, MetaDeliveryStatus } from '../types';
import { META_STATUS_TO_RAW } from '../types';

export type SentDocumentSortField = 'created_at' | 'sent_at' | 'delivered_at';
export type SortOrder = 'asc' | 'desc';

/** Default multi-select values (all selected except appointment) */
export const DEFAULT_SENT_DOCUMENT_TYPES = ['form', 'document', 'lab'] as const;
export const DEFAULT_CHANNELS = ['sms', 'email'] as const;
export const DEFAULT_STATUSES = ['pending', 'delivered', 'failed'] as const;

export interface SentDocumentFiltersState {
  patientId: string | null;
  communicationTypes: string[];
  channels: string[];
  statuses: string[];
  startDate: Dayjs | null;
  endDate: Dayjs | null;
}

export interface UseSentDocumentsListOptions {
  perPage?: number;
}

export interface UseSentDocumentsListReturn {
  // Data
  groups: SentDocumentGroup[];
  isLoading: boolean;
  isFetching: boolean;
  error: string | null;

  // Cursor pagination
  hasNextPage: boolean;
  hasPreviousPage: boolean;

  // Filters & sorting
  filters: SentDocumentFiltersState;
  sortBy: SentDocumentSortField;
  sortOrder: SortOrder;

  // State helpers
  isEmpty: boolean;
  hasActiveFilters: boolean;

  // Actions
  goNextPage: () => void;
  goPreviousPage: () => void;
  setPatientId: (patientId: string | null) => void;
  setCommunicationTypes: (types: string[]) => void;
  setChannels: (channels: string[]) => void;
  setStatuses: (statuses: string[]) => void;
  setStartDate: (date: Dayjs | null) => void;
  setEndDate: (date: Dayjs | null) => void;
  setSortBy: (field: SentDocumentSortField) => void;
  setSortOrder: (order: SortOrder) => void;
  toggleSort: (field: SentDocumentSortField) => void;
  clearFilters: () => void;
  refresh: () => void;
}

const DEFAULT_PER_PAGE = 20;

/**
 * Map API CommunicationItem to SentDocumentGroup (add id for DataGrid).
 */
function toDisplayGroup(item: CommunicationItem): SentDocumentGroup {
  return {
    id: item.dispatch_id ?? item.channels[0]?.touchpoint_id ?? item.created_at,
    dispatch_id: item.dispatch_id ?? null,
    communication_type: item.communication_type,
    name: item.name ?? null,
    patient: item.patient ?? null,
    patient_id: item.patient_id,
    document_id: item.document_id ?? null,
    form_status: item.form_status ?? null,
    source: item.source,
    created_at: item.created_at,
    channels: item.channels,
  };
}

export function useSentDocumentsList(
  options: UseSentDocumentsListOptions = {},
): UseSentDocumentsListReturn {
  const { perPage = DEFAULT_PER_PAGE } = options;

  // Multi-select filter state (empty = all / no filter)
  const [patientId, setPatientIdState] = useState<string | null>(null);
  const [communicationTypes, setCommunicationTypesState] = useState<string[]>([]);
  const [channels, setChannelsState] = useState<string[]>([]);
  const [statuses, setStatusesState] = useState<string[]>([]);
  const [startDate, setStartDateState] = useState<Dayjs | null>(null);
  const [endDate, setEndDateState] = useState<Dayjs | null>(null);

  // Sort state
  const [sortBy, setSortByState] = useState<SentDocumentSortField>('created_at');
  const [sortOrder, setSortOrderState] = useState<SortOrder>('desc');

  // Simple page-based pagination (API handles grouping now)
  const [currentPage, setCurrentPage] = useState(1);

  // RTK Query lazy query
  const [trigger, { data, isLoading, isFetching, error: queryError }] =
    useLazyGetSentDocumentsQuery();

  // Track if initial fetch has happened
  const hasFetched = useRef(false);

  // Build query args
  const queryArgs = useMemo(
    () => ({
      page: currentPage,
      perPage,
      patientId: patientId || undefined,
      communicationType: communicationTypes.length > 0
        ? communicationTypes.join(',')
        : [...DEFAULT_SENT_DOCUMENT_TYPES].join(','),
      channel: channels.length > 0 ? channels.join(',') : undefined,
      status: statuses.length > 0
        ? statuses.flatMap(s => META_STATUS_TO_RAW[s as MetaDeliveryStatus] ?? [s]).join(',')
        : undefined,
      startDate: startDate?.toISOString() || undefined,
      endDate: endDate?.toISOString() || undefined,
      sortBy,
      sortOrder,
    }),
    [
      currentPage,
      perPage,
      patientId,
      communicationTypes,
      channels,
      statuses,
      startDate,
      endDate,
      sortBy,
      sortOrder,
    ],
  );

  // Trigger fetch when queryArgs change
  useEffect(() => {
    trigger(queryArgs);
    hasFetched.current = true;
  }, [queryArgs, trigger]);

  // Map API items to display groups
  const displayGroups = useMemo(() => {
    if (!data?.items) return [];
    return data.items.map(toDisplayGroup);
  }, [data]);

  const hasNextPage = (data?.page ?? 1) < (data?.total_pages ?? 1);
  const hasPreviousPage = currentPage > 1;

  // Derived values
  const filters: SentDocumentFiltersState = useMemo(
    () => ({ patientId, communicationTypes, channels, statuses, startDate, endDate }),
    [patientId, communicationTypes, channels, statuses, startDate, endDate],
  );

  const hasActiveFilters = useMemo(
    () =>
      Boolean(
        patientId ||
          communicationTypes.length > 0 ||
          channels.length > 0 ||
          statuses.length > 0 ||
          startDate ||
          endDate,
      ),
    [patientId, communicationTypes, channels, statuses, startDate, endDate],
  );

  // Reset page to 1
  const resetPage = useCallback(() => {
    setCurrentPage(1);
  }, []);

  // Navigation
  const goNextPage = useCallback(() => {
    if (hasNextPage) {
      setCurrentPage(prev => prev + 1);
    }
  }, [hasNextPage]);

  const goPreviousPage = useCallback(() => {
    if (hasPreviousPage) {
      setCurrentPage(prev => prev - 1);
    }
  }, [hasPreviousPage]);

  // Filter setters (all reset page)
  const setPatientId = useCallback(
    (id: string | null) => {
      setPatientIdState(id);
      resetPage();
    },
    [resetPage],
  );

  const setCommunicationTypes = useCallback(
    (types: string[]) => {
      setCommunicationTypesState(types);
      resetPage();
    },
    [resetPage],
  );

  const setChannels = useCallback(
    (chs: string[]) => {
      setChannelsState(chs);
      resetPage();
    },
    [resetPage],
  );

  const setStatuses = useCallback(
    (sts: string[]) => {
      setStatusesState(sts);
      resetPage();
    },
    [resetPage],
  );

  const setStartDate = useCallback(
    (date: Dayjs | null) => {
      setStartDateState(date);
      resetPage();
    },
    [resetPage],
  );

  const setEndDate = useCallback(
    (date: Dayjs | null) => {
      setEndDateState(date);
      resetPage();
    },
    [resetPage],
  );

  const setSortBy = useCallback(
    (field: SentDocumentSortField) => {
      setSortByState(field);
      resetPage();
    },
    [resetPage],
  );

  const setSortOrder = useCallback(
    (order: SortOrder) => {
      setSortOrderState(order);
      resetPage();
    },
    [resetPage],
  );

  const toggleSort = useCallback(
    (field: SentDocumentSortField) => {
      if (sortBy === field) {
        setSortOrderState(sortOrder === 'asc' ? 'desc' : 'asc');
      } else {
        setSortByState(field);
        setSortOrderState('asc');
      }
      resetPage();
    },
    [sortBy, sortOrder, resetPage],
  );

  const clearFilters = useCallback(() => {
    setPatientIdState(null);
    setCommunicationTypesState([]);
    setChannelsState([]);
    setStatusesState([]);
    setStartDateState(null);
    setEndDateState(null);
    resetPage();
  }, [resetPage]);

  const refresh = useCallback(() => {
    trigger(queryArgs);
  }, [trigger, queryArgs]);

  return {
    groups: displayGroups,
    isLoading: isLoading || !hasFetched.current,
    isFetching,
    error: queryError ? 'Failed to load sent documents. Please try again.' : null,

    hasNextPage,
    hasPreviousPage,

    filters,
    sortBy,
    sortOrder,

    isEmpty: !isLoading && hasFetched.current && displayGroups.length === 0,
    hasActiveFilters,

    goNextPage,
    goPreviousPage,
    setPatientId,
    setCommunicationTypes,
    setChannels,
    setStatuses,
    setStartDate,
    setEndDate,
    setSortBy,
    setSortOrder,
    toggleSort,
    clearFilters,
    refresh,
  };
}
